import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../cn';

// Mirrors apps/pma-client/src/components/ui/Button.tsx so primary/ghost/etc.
// look identical across PMA, accounting, training, and any future app.
//
// Every variant carries a 1px border so filled and outlined buttons land
// at the same height. Filled variants use a transparent border; outlined
// override the color.

const buttonStyles = cva(
    'inline-flex items-center justify-center gap-1.5 font-medium rounded-md border border-transparent transition-colors duration-[var(--duration-base)] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap',
    {
        variants: {
            variant: {
                primary: 'bg-text text-text-inverse hover:bg-[#333]',
                success: 'bg-accent-dark text-text font-bold hover:bg-[#5a8a6a]',
                danger:  'bg-error-soft text-error border-[rgb(192_57_43_/_0.25)] hover:bg-[rgb(192_57_43_/_0.14)]',
                ghost:   'bg-surface text-text border-border hover:bg-surface-muted',
                link:    'border-transparent text-text underline-offset-4 hover:underline px-0',
            },
            size: {
                xs: 'text-[11px] px-2 py-0.5 h-6',
                sm: 'text-xs px-2.5 py-1 h-7',
                md: 'text-sm px-4 py-1.5 h-8',
                lg: 'text-sm px-5 py-2 h-10',
            },
        },
        defaultVariants: { variant: 'primary', size: 'md' },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
    asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                ref={ref}
                className={cn(buttonStyles({ variant, size }), className)}
                {...props}
            />
        );
    },
);
Button.displayName = 'Button';
